/* eslint-disable @typescript-eslint/naming-convention */
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { HotToastService } from '@ngneat/hot-toast';
import { BadgeOptions } from 'app/components/badge/badge.interface';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { PickerSelectDisableOptions, PickerSelectOptions } from '../data-picker.interface';
import _ from 'lodash';
import { MultiModulePickerService } from 'app/components/multi-module-picker/multi-module-picker.service';

@Component({
  selector: 'asset-data-picker',
  templateUrl: './asset-data-picker.component.html',
  styleUrls: ['./asset-data-picker.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AssetDataPickerComponent implements OnInit, OnChanges {
  @Input() filter: any = null;
  @Input() selectOption: PickerSelectOptions = PickerSelectOptions.select;
  @Output() eventEmitter: EventEmitter<any> = new EventEmitter();
  @Input() selectedAssets: any[] = [];
  @Input() searchKey: string = '';
  @Input() pickerConfig: any = null;
  @Input() assetUrl: any = '';
  @Input() pageIndex: number = 0;
  @Input() showSelectedItems: boolean = false;
  @Input() sortBy: string = 'asset_code';
  @Input() sortType: string = 'ASC';
  @Input() customParams: any = null;
  @Input() filterFormGroup: UntypedFormGroup;
  @Input() assetData: any[] = null;
  @Input() selectionDisable: { selectionDisableBasedOn: PickerSelectDisableOptions; elementId?: string } = null; //NA for readonly selectOption
  @Input() hasFilterValue: boolean = false;
  @Input() associatedAssetsArr: any = null;
  @Input() isFromNote: boolean = false;
  @Input() module: string = null;
  @Input() moduleUID: string = null;

  duplicatedAssetData: any[] = [];
  assets: any[] = [];
  assetsList: any[] = [];
  assetEleId: string = '';
  assetDetails: any = null;
  loaded: boolean = false;
  onSearch: boolean = true;
  localeKeys: any = {
    expand: 'expand',
    collapse: 'collapse'
  };
  defaultParamObj: any = {
    'sort': this.sortType,
    'sort_by': this.sortBy,
    'filter.is_active': true
  };
  isSpinLoading: boolean = false;
  currency: string;
  digitsInfo: string = '1.2-2';
  isAssociatedExpanded: boolean = true;
  notesPickerPreferences: any = {};
  associatedAssets: any[] = [];

  constructor(
    private utilityService: UtilityService,
    private dataCollectorService: DataCollectorService,
    private localisationService: LocalisationService,
    private multiModulePickerService: MultiModulePickerService,
    private toast: HotToastService) {
  }

  ngOnInit(): void {
    this.notesPickerPreferences = this.multiModulePickerService.notesPickerPreferences;
    this.currency = this.utilityService.companyCurrency;
    this.localisationService.setDefaultLanguage();
    if (this.selectOption && this.selectOption === PickerSelectOptions.readOnly) {
      delete this.defaultParamObj?.['filter.is_active'];
    }
    this.filterFormGroup?.valueChanges.subscribe((data: any) => {
      this.updateDataParams(data);
    });
    this.showDataPickerPanel();
  }

  rotateArrowIcon(assetUID: any, isAssociated?: boolean): any {
    const isExpanded: boolean = this.isExpandingAction(assetUID, isAssociated);
    return {
      'rotate-arrow-icon-down': isExpanded,
      'rotate-arrow-icon-up': !isExpanded
    };
  }

  showSpinLoading(assetUID: any, isAssociated?: boolean): boolean {
    return this.isExpandingAction(assetUID, isAssociated) && this.isSpinLoading;
  }

  updateArrowTooltipText(assetUID: any, isAssociated?: boolean): string {
    return this.isExpandingAction(assetUID, isAssociated) ? this.localeKeys.collapse : this.localeKeys.expand;
  }

  isExpandingAction(assetUID: any, isAssociated?: boolean): boolean {
    return (this.assetEleId === 'asset_details_' + assetUID && !isAssociated) || (this.assetEleId === 'asset_details_associated_' + assetUID && isAssociated);
  }

  getAssetStatusValue(status: string): string {
    return status.trim().replaceAll('_', ' ');
  }

  viewAssetFullDetails(assetUID: string): string {
    return '/asset_management/' + assetUID + '/details';
  }

  viewAssetDetails(event: any, assetUID: string, isAssociated?: boolean): void {
    event.stopPropagation();
    const elementId: string = (isAssociated ? 'asset_details_associated_' : 'asset_details_') + assetUID;
    if (this.assetEleId === elementId) {
      this.resetAssetDetails();
    } else {
      this.assetEleId = elementId;
      this.getAssetDetails(assetUID);
    }
  }

  getAssetDetails(assetUID: string): void {
    this.isSpinLoading = true;
    /* eslint-disable quotes */
    this.dataCollectorService.getData(this.assetUrl + `/` + assetUID).subscribe(
      /* eslint-enable quotes */
      (assets: any) => {
        this.assetDetails = assets.data;
        this.isSpinLoading = false;
      }, (_error: any) => {
        this.resetAssetDetails();
        this.isSpinLoading = false;
      });
  }

  resetAssetDetails(): void {
    this.assetEleId = '';
    this.assetDetails = null;
  }

  isSelectedAsset(asset: any): boolean {
    if (this.selectedAssets?.length) {
      return this.selectedAssets.find(value => value.asset_uid === asset.asset_uid) ? true : false;
    }
    return false;
  }

  selectAsset(event: any, asset: any): void {
    if (this.selectOption === PickerSelectOptions.multiSelect) {
      if (event?.target?.checked === true) {
        this.selectedAssets.push(asset);
      } else {
        this.selectedAssets = this.selectedAssets.filter(value => value.asset_uid !== asset.asset_uid);
      }
    } else {
      this.selectedAssets = [asset];
    }
    this.eventEmitter.emit({ value: this.selectedAssets});
  }

  triggerSearch(searchKey: any): void {
    this.onSearch = true;
    if (this.assetData) {
      this.assetDataSearch(searchKey);
    } else {
      this.assetRemoteDataSearch(searchKey);
    }
  }

  assetDataSearch(searchKey: any): void {
    if (!searchKey) {
      this.assets = this.duplicatedAssetData;
    } else {
      const filteredValue: any[] = this.duplicatedAssetData?.filter((asset: any) => {
        searchKey = searchKey.toLowerCase();
        const assetCode: string = asset?.asset_code?.toLowerCase();
        const assetName: string = asset?.asset_name?.toLowerCase();
        if (assetCode?.indexOf(searchKey) > -1 || assetName?.indexOf(searchKey) > -1) {
          return asset;
        }
      });
      this.assets = filteredValue?.length ? [...filteredValue] : [];
    }
  }

  assetRemoteDataSearch(searchKey: any): void {
    delete this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey];
    if (this.defaultParamObj[this.pickerConfig.params.searchKey] !== searchKey) {
      const pageIndex: number = 0;
      this.defaultParamObj[this.pickerConfig.params.searchKey] = searchKey;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  sortingAction(key: string, value: string): void {
    if (value) {
      const pageIndex: number = this.pageIndex ?? 0;
      this.defaultParamObj[key] = value;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  getData(pageIndex: number, isOpen?: boolean): void {
    this.assetEleId = '';
    this.loaded = false;
    this.defaultParamObj.page = pageIndex + 1;
    const urlParams = this.utilityService.constructParam(this.defaultParamObj);
    if(this.notesPickerPreferences[this.module]?.preference?.asset === 'LINKED'){
      urlParams.updates.push({
        "param": this.notesPickerPreferences[this.module]?.filter,
        "value": this.moduleUID,
        "op": "s"
      });
    } else if(this.notesPickerPreferences[this.module]?.preference?.asset === 'BOTH' && this.isFromNote){
        this.updateAssociatedData();
    }
    this.dataCollectorService.getData(this.assetUrl, urlParams).subscribe(
      (response: any) => {
        this.assetsList = response.data;
        this.assets = response.data;
        this.loaded = true;
        this.eventEmitter.emit({ totalRecords: response.total_records });
        if (isOpen && this.filter && this.assets.length) {
          this.selectedAssets = this.selectedAssets?.length > 0 ? _.uniqBy([...this.selectedAssets, ...this.filter], 'asset_uid') : this.filter;
          this.eventEmitter.emit({ value: this.selectedAssets });
        } else if (isOpen && this.assets.length === 0) {
          this.onSearch = false;
        }
      }, (_error: any) => {
        this.loaded = true;
      });
  }

  getAssociatedData(urlParams): void {
    urlParams[this.notesPickerPreferences[this.module].filter] = this.moduleUID;
    this.dataCollectorService.getData(this.assetUrl, urlParams).subscribe(
      (response: any) => {
        this.associatedAssets = response.data;
      }, (_error: any) => {
      });
  }

  updateAssociatedData(): any{
    if(this.associatedAssetsArr?.length > 0 || this.module === 'JOB' || this.module === 'PROJECT'){
      const keyword = this.defaultParamObj['filter.keyword']?.toLowerCase() || '';
      this.associatedAssets = this.associatedAssetsArr?.filter(asset =>
        Object.values(asset).some(value =>
          (typeof value === 'string' || typeof value === 'number') &&
          value.toString().toLowerCase().includes(keyword)
        )
      );
    } else{
      const urlParams =  {
        'sort': 'DESC',
        'sort_by': 'created_at',
        'filter.is_active': true,
        'page': 1,
        'count': 20,
        'filter.keyword':  this.defaultParamObj['filter.keyword'],
      };
      this.getAssociatedData(urlParams);
    }
  }

  showDataPickerContent(): void {
    if (this.hasFilterValue) {
      return null;
    }
    const pageIndex: number = this.pageIndex ?? 0;
    Object.keys(this.pickerConfig.params).forEach((key: any) => {
      const paramKey: any = this.pickerConfig.params[key];
      if (key !== 'uniqueSearchKey') {
        let value: any = '';
        if (key === 'countKey') {
          value = 10;
        } else if (key === 'pageKey') {
          value = 1;
        } else if (key === 'searchKey' && !this.showSelectedItems && this.searchKey) {
          value = this.searchKey;
        }
        this.defaultParamObj[paramKey] = value;
      }
    });
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    if (this.filter) {
      this.updateFilterValue();
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
    this.eventEmitter.emit({ sortBy: this.defaultParamObj['sort_by'], sortType: this.defaultParamObj['sort'] });
    this.getData(pageIndex, true);
  }

  updateFilterValue(): void {
    if (!this.assetData && this.selectOption === PickerSelectOptions.select && this.filter.length > 0) {
      if (this.filter[0].asset_name) {
        this.defaultParamObj[this.pickerConfig.params.searchKey] = this.filter[0].asset_name;
        this.eventEmitter.emit({ searchKey: this.filter[0].asset_name });
      } else {
        this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey] = this.filter[0].asset_uid;
      }
    }
  }

  updateDataParams(formControlValues: any): void {
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    const params: any = this.defaultParamObj ?? {};
    if (formControlValues.moduleBasedFilterValue) {
      let hasModuleFilterValue: boolean = false;
      delete params['filter.orphaned_assets'];
      formControlValues.moduleBasedFilterValue.forEach((value: any) => {
        if (value.value) {
          params[value?.key] = value.value;
          hasModuleFilterValue = true;
        } else {
          delete params[value?.key];
        }
      });
      if (!hasModuleFilterValue) {
        params['filter.orphaned_assets'] = true;
      }
    }

    if (formControlValues.assetStatus) {
      params['filter.asset_status'] = formControlValues.assetStatus?.value;
    } else {
      delete params['filter.asset_status'];
    }
    this.updateFilters();
  }

  updateFilters(): void {
    const pageIndex: number = 0;
    this.getData(pageIndex, true);
    this.eventEmitter.emit({ pageIndex: pageIndex });
  }

  updateAssetData(): void {
    this.assets = this.assetData;
    this.duplicatedAssetData = [...this.assetData];
    this.onSearch = false;
    this.loaded = true;
    if (this.filter && this.assetData?.length) {
      this.selectedAssets = this.filter;
      this.eventEmitter.emit({ value: this.selectedAssets });
    }
    if (this.searchKey) {
      this.eventEmitter.emit({ searchKey: this.searchKey });
      this.triggerSearch(this.searchKey);
    }
  }

  resetAssetDataPicker(): void {
    this.assets = [];
  }

  showDataPickerPanel(): void {
    this.resetAssetDataPicker();
    if (this.assetData) {
      this.updateAssetData();
    } else if (this.showSelectedItems) {
      this.assets = this.selectedAssets;
    } else if (this.assetsList.length === 0 && this.searchKey) {
      this.showDataPickerContent();
    } else if (this.assetsList.length > 0 || this.searchKey) {
      this.assets = this.assetsList;
    } else {
      this.showDataPickerContent();
    }
  }

  hideSelectedItemsPanel(): void {
    this.eventEmitter.emit({ showSelectedItems: false, value: this.selectedAssets, filter: this.selectedAssets });
  }

  validateSelectionDisable(asset: any): boolean {
    return (this.selectionDisable?.selectionDisableBasedOn === PickerSelectDisableOptions.ASSET && asset?.asset_uid === this.selectionDisable?.elementId);
  }

  getBadgeOptions(type: string, displayName?: string, displayKey?: any, displayType?: string): BadgeOptions {
    return {
      type: type,
      displayKey: displayKey ?? null,
      displayName: displayName ?? (type ? type.toLowerCase() : type),
      displayType: displayType ?? null
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.searchKey?.firstChange === false) {
      this.triggerSearch(changes?.searchKey?.currentValue);
    }

    if (changes?.pageIndex?.previousValue !== undefined) {
      this.isAssociatedExpanded = false;
      this.getData(changes?.pageIndex?.currentValue);
    }

    if (changes?.showSelectedItems?.firstChange === false || changes?.assetData?.firstChange === false) {
      this.showDataPickerPanel();
    }

    if (changes?.sortBy?.firstChange === false && changes?.sortBy?.previousValue !== undefined) {
      this.sortingAction('sort_by', changes?.sortBy?.currentValue);
    }

    if (changes?.sortType?.firstChange === false && changes?.sortType?.previousValue !== undefined) {
      this.sortingAction('sort', changes?.sortType?.currentValue);
    }
  }
}
